import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { MAX_SERVICE_DESK_CC, parseEmailList } from '@nsc/smartteams-shared/api/service-desk-email.js';
import { html, nothing } from 'lit';

/**
 * To / Cc recipients for the smartteams-app "Email service desk" button, edited per partner company.
 * An info alert at the top tells admins what the fields are for and what happens when they are empty.
 * Rendered only for NSC Admin (the section is gated on ServiceDeskSettingsManage in the entity config).
 *
 * Accepts Outlook-style input (`a@x.com; b@x.com`, commas or new lines). Nothing is saved while the
 * text has invalid addresses — the error is shown instead — so a half-typed value can never reach the
 * database; the server entity validation repeats the same rules.
 */
export const ServiceDeskEmailSettings = stateful(
  ({
    to,
    cc,
    onChange,
  }: {
    to: string;
    cc: string[];
    onChange: (value: { to: string; cc: string[] }) => void;
  }) => {
    const [toText, setToText] = useState<string>(to);
    const [ccText, setCcText] = useState<string>(cc.join('\n'));
    const [error, setError] = useState<string>('');

    const commit = (nextTo: string, nextCc: string) => {
      const parsedTo = parseEmailList(nextTo);
      const parsedCc = parseEmailList(nextCc);
      const invalid = [...parsedTo.invalid, ...parsedCc.invalid];
      if (invalid.length > 0) return setError(`Not a valid email address: ${invalid.join(', ')}`);
      if (parsedTo.emails.length > 1) return setError('Enter exactly one address in To');
      if (parsedCc.emails.length > MAX_SERVICE_DESK_CC) return setError(`At most ${MAX_SERVICE_DESK_CC} Cc addresses`);
      setError('');
      onChange({ to: parsedTo.emails[0] ?? '', cc: parsedCc.emails });
    };

    return html`
      <div class="flex flex-col gap-3">
        <div class="alert alert-info text-sm py-2 items-start" role="note">
          <span class="icon-[mdi--information-outline] text-base shrink-0 mt-0.5"></span>
          <div class="flex flex-col gap-1">
            <span class="font-medium">What these addresses are for</span>
            <span>
              They are used by the "Email service desk" button that this company's field engineers see on their
              tickets (Tickets and Colleagues). <strong>To</strong> is the main recipient and <strong>Cc</strong>
              is copied. The engineer's own mail program opens with the mail prefilled and they send it themselves;
              SmartTeams sends nothing.
            </span>
            <span>
              If you leave these empty, the button still opens the mail program, with the subject and text filled in
              and the address left blank for the engineer to type.
            </span>
          </div>
        </div>
        <label class="flex flex-col gap-1">
          <span class="text-sm font-medium">To</span>
          <input
            type="email"
            class="input input-bordered w-full"
            placeholder="support@example.com"
            .value=${toText}
            @input=${(e: Event) => setToText((e.target as HTMLInputElement).value)}
            @change=${() => commit(toText, ccText)}
          />
        </label>
        <label class="flex flex-col gap-1">
          <span class="text-sm font-medium">Cc</span>
          <textarea
            class="textarea textarea-bordered w-full"
            rows="3"
            placeholder="One per line, or separated by commas or semicolons"
            .value=${ccText}
            @input=${(e: Event) => setCcText((e.target as HTMLTextAreaElement).value)}
            @change=${() => commit(toText, ccText)}
          ></textarea>
        </label>
        ${error ? html`<p class="text-sm text-error">${error}</p>` : nothing}
      </div>
    `;
  },
);
